import { Typography } from '@material-ui/core';
import {
  Theme,
  createStyles,
  fade,
  makeStyles
} from '@material-ui/core/styles';
import { CloudUploadRounded } from '@material-ui/icons';
import clsx from 'clsx';
import React, { MutableRefObject } from 'react';
import { DropzoneInputProps, DropzoneRootProps } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

interface DropzoneAreaProps {
  dropzoneArea: MutableRefObject<HTMLDivElement | null>;
  getRootProps: <T extends DropzoneRootProps>(props?: T | undefined) => T;
  getInputProps: <T extends DropzoneInputProps>(props?: T | undefined) => T;
  maxFileSizeInMB: number;
  inputKey: number;
  inDropzone?: boolean;
  disabled?: boolean;
}

export default function DropzoneArea({
  dropzoneArea,
  getRootProps,
  getInputProps,
  maxFileSizeInMB,
  inputKey,
  inDropzone = true,
  disabled = false
}: DropzoneAreaProps) {
  const classes = useStyles();
  const { t } = useTranslation();

  return (
    <div
      ref={dropzoneArea}
      style={{ display: 'none' } as React.CSSProperties}
      className={classes.container}
    >
      <div
        className={clsx(classes.dropZone, {
          [classes.dropZoneActive]: inDropzone,
          [classes.dropZoneDisable]: disabled
        })}
      >
        <div
          {...getRootProps({ className: 'dropzone' })}
          className={classes.uploadBox}
        >
          <input key={inputKey} {...getInputProps()} disabled={disabled} />
          <CloudUploadRounded
            color={!disabled ? 'primary' : 'disabled'}
            fontSize="large"
          />
          <Typography color="textPrimary" variant="body2">
            {t('dropzoneArea.dropFilesToUpload')}
          </Typography>
          <div className={classes.textCaption}>
            <Typography variant="caption">
              {t('dropzoneArea.maximumSize', { size: maxFileSizeInMB })}
            </Typography>
          </div>
        </div>
      </div>
    </div>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      position: 'absolute',
      left: 0,
      top: 0,
      width: '100%',
      height: '100%',
      borderRadius: theme.spacing(2),
      backgroundColor: theme.palette.background.paper
    },
    dropZone: {
      width: '100%',
      height: '100%',
      color: theme.palette.text.hint,
      borderColor: '#dddddd',
      borderRadius: theme.spacing(2),
      borderStyle: 'dashed',
      borderWidth: 2,
      backgroundColor: theme.palette.background.paper,
      transition: '0.4s'
    },
    dropZoneActive: {
      borderColor: theme.palette.primary.main,
      backgroundColor: fade(theme.palette.primary.main, 0.1)
    },
    dropZoneDisable: {
      borderColor: '#dddddd',
      backgroundColor: fade(theme.palette.background.default, 0.75)
    },
    uploadBox: {
      width: '100%',
      height: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      flexDirection: 'column',
      padding: theme.spacing(1, 2, 3, 2)
    },
    textCaption: {
      marginTop: theme.spacing(2)
    }
  })
);
